import {keyframes} from '@emotion/react'
import {remCalc} from '_themeUtils'

// 09AC text animation: the content block rises slightly while fading in (the design system's "fade-up").
const fadeUpAnimation = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

// Default duration (ms) when animation_duration is not provided — 1300ms per the 09AC module doc.
const DEFAULT_ANIMATION_DURATION = '1300'

/**
 * Styles for the 09C / 09AC "Half Text Half Photo" Page Designer module.
 *
 * Layout (from the Brunello Cucinelli design system, node 8500-12610): a 50/50 split — one half is the
 * Cloudinary media (image or video), the other half is the text block (title / text / CTA). On desktop
 * and tablet-lg the two halves sit side by side (6/6 of a 12-col grid); on mobile they stack vertically.
 * `reverseColumns` swaps which half comes first (mirrors SFRA `reverse_columns` / the Figma Image-vs-Text
 * hierarchy). The text half is padded and its content centered within, matching the design.
 */
const halfTextHalfPhoto = {
    HalfTextHalfPhoto: {
        baseStyle: ({sectionBackground, contentColor, reverseColumns, isTextAnimated, animationDuration, verticalAlign, horizontalAlign}) => ({
            wrapper: {
                bg: sectionBackground ? sectionBackground : 'color.background.transparent',
                w: '100%',
                gridColumn: '1 / -1', // span the full editorial container width
            },
            // Two 50/50 halves via flexbox. On mobile they stack vertically (column); on md+ they sit
            // side by side (row). The swap is done DETERMINISTICALLY via flex-direction (not per-item
            // `order`, which is unreliable on a CSS grid). The media half is FIRST in the DOM.
            //
            // Direction matched to the SFRA production render (verified against the live DOM for the refill
            // module, which delivers reverse_columns=true): in SFRA the two halves are laid out
            // media-then-text and `reverse_columns=true` shows IMAGE LEFT / TEXT RIGHT (image top / text
            // bottom on mobile). With our media-first DOM that is the NATURAL row/column; the flag flips it.
            //   reverse_columns=true  → image leads  → row / column
            //   reverse_columns=false → image trails → row-reverse / column-reverse
            grid: {
                display: 'flex',
                flexDirection: reverseColumns ? {base: 'column', md: 'row'} : {base: 'column-reverse', md: 'row-reverse'},
                gap: 0,
                alignItems: 'stretch',
            },
            // The media half (image/video). Half width on md+, full width on mobile. Order in the flow is
            // handled by the container's flex-direction, not per-item order.
            mediaItem: {
                flex: {base: '0 0 auto', md: '0 0 50%'},
                w: {base: '100%', md: '50%'},
                minH: {base: remCalc(375), md: 'unset'},
                overflow: 'hidden',
            },
            // The text half. Padding per the Figma design (node 8500-12610), per breakpoint:
            // mobile 24 / tablet-sm 30 / tablet-lg 30 / desktop 60. Breakpoints map: base=mobile (<768),
            // md=tablet-sm (768–1023), lg=tablet-lg (1024–1199), xl=desktop (1200+). tablet-sm and
            // tablet-lg share 30, so a single `md: 30` covers both up to the desktop `xl: 60`.
            // content_v_align / content_h_align position the text block within the half; both default to
            // center (Figma default variant: Content ↑↓ Center, Content ←→ Center).
            textItem: {
                flex: {base: '0 0 auto', md: '0 0 50%'},
                w: {base: '100%', md: '50%'},
                display: 'flex',
                flexDirection: 'column',
                alignItems: horizontalAlign === 'left' ? 'flex-start' : horizontalAlign === 'right' ? 'flex-end' : 'center',
                justifyContent: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
                bg: sectionBackground ? sectionBackground : 'color.background.transparent',
                color: contentColor && contentColor.length ? contentColor : 'color.text.default',
                p: {base: remCalc(24), md: remCalc(30), xl: remCalc(60)},
            },
            // Text block: title (display-xs) + description (paragraph-lg) + CTA, gap 15px (constant across
            // breakpoints per the design). Max width per Figma per breakpoint: mobile 300 / tablet-sm 250 /
            // tablet-lg 280 / desktop 400. Color inherited so contentColor applies to every child.
            // text-align follows content_t_align on the components.
            body: {
                gap: remCalc(15),
                maxW: {base: remCalc(300), md: remCalc(250), lg: remCalc(280), xl: remCalc(400)},
                color: 'inherit',
                // 09AC fade-up: applied to the whole text block so title, description and CTA rise/fade
                // together. Gated by isTextAnimated (animated variant AND in viewport); 'none' otherwise
                // so the static 09C is unaffected. cubic-bezier matches CardPhotoVideo's text animation.
                animation: isTextAnimated ? `${fadeUpAnimation} ${animationDuration || DEFAULT_ANIMATION_DURATION}ms cubic-bezier(.25,.1,.25,1)` : 'none',
            },
            // Title: GT Eesti Regular, display-xs (24/28, 0.48px) at every breakpoint per the design.
            title: {
                textStyle: 'displayXs',
                fontFamily: 'GTEesti',
                fontWeight: 400,
                color: 'inherit',
            },
            // Description: GT Eesti Light, paragraph-lg (18/24, 0.9px) at every breakpoint per the design.
            text: {
                textStyle: 'paragraphLg',
                color: 'inherit',
            },
        }),
    },
}

export default halfTextHalfPhoto
